import { useEffect } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";

/**
 * MissionCompleteOverlay — shown after a student saves a mission.
 *
 * Lightweight, classroom-friendly. No sound. CSS-only entrance. Auto-focus
 * on the Continue button so keyboard users can dismiss with Enter.
 *
 * Usage: render conditionally on the calling mission page; the page also
 * owns the post-dismiss navigation via `onContinue`.
 */
export default function MissionCompleteOverlay({
  missionNumber,
  missionTitle,
  message,
  continueLabel = "Continue",
  onContinue,
}) {
  useEffect(() => {
    // Lock background scroll while the overlay is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-gradient-to-br from-yellow-900/40 via-orange-900/40 to-amber-900/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Mission ${missionNumber} complete`}
      data-testid="mission-complete-overlay"
    >
      <div className="bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full ring-2 ring-yellow-300 shadow-2xl text-center animate-in fade-in zoom-in duration-300">
        <div className="bg-green-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-yellow-700 mb-2">
          Mission {missionNumber} Complete!
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
          {missionTitle}
        </h2>
        {message && (
          <p className="text-gray-600 leading-relaxed mb-6">{message}</p>
        )}
        <button
          type="button"
          autoFocus
          onClick={onContinue}
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-full font-bold text-base transition-colors shadow-md"
          data-testid="mission-complete-continue"
        >
          {continueLabel}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
